import { useCallback, useEffect, useState } from "react";

// Add the Firebase products that you want to use
import "firebase/auth";
import Auth from "./Auth";
import SudowriteLogo from "./components/SudowriteLogo";
import useCookieRunOnce from "./hooks/useCookieRunOnce";
import { SudoButton } from "./sudokit/components/SudoButton";
import { DISCORD_LINK } from "./Utils";
import { requiresConferencePayment } from "../../src/shared/conferenceCampaign";

export default function Checkout({ subscription, user }) {
  const [nextUrl, setNextUrl] = useState(null);
  const [checkoutSessionId, setCheckoutSessionId] = useState(null);
  const [finished, setFinished] = useState(false);
  const [checkoutCompletedPrior, setCheckoutCompletedPrior] = useState(false);

  const runOnce = useCookieRunOnce();

  useEffect(() => {
    const url = new URL(window.location.href);
    const query = new URLSearchParams(url.search);
    // console.log(query);

    setNextUrl(query.get("next_url"));
    setCheckoutSessionId(query.get("session_id"));
    setCheckoutCompletedPrior(query.get("sscid"));
  }, []);

  const checkFinished = useCallback(async () => {
    if (checkoutSessionId && user) {
      let result;
      try {
        result = await Auth.fetchWithIdToken(`/api/subscriptions/${user.uid}/getCheckoutSession`, {
          checkoutSessionId,
        });
      } catch (e) {
        setTimeout(() => {
          checkFinished();
        }, 3000);
        return;
      }
      const data = result.object;

      if (data?.status === "complete") {
        setFinished(true);

        // check that it's a subscription
        if (data.subscription) {
          const value = data.amount_total / 100.0; // dollar amount
          const description = data.invoice?.lines.data[0]?.description;
          const key = "sent-google-checkout-" + checkoutSessionId;

          runOnce(() => {
            // console.log("pushing successful checkout to datalayer");
            try {
              if (window.dataLayer) {
                // push to gtag data layer only if we haven't done it yet for this client
                window.dataLayer.push({
                  event: "subscribe",
                  eventCategory: "Subscription",
                  eventAction: "Subscribe",
                  eventLabel: description,
                  eventValue: value,
                  currency: "USD",
                });
              }
              if (window.edgetag) {
                // console.log("[EDGETAG SUBSCRIBE]");
                window.edgetag("tag", "Subscribe", {
                  currency: "USD",
                  value: value,
                });
              }
            } catch (e) {
              // do nothing
            }
          }, key);
        }
      } else {
        setTimeout(() => {
          checkFinished();
        }, 3000);
      }
    }
  }, [checkoutSessionId, user]);

  useEffect(() => {
    if (checkoutSessionId && user) {
      checkFinished();
    }
  }, [checkoutSessionId, user]);

  useEffect(() => {
    if (checkoutCompletedPrior && user) {
      setFinished(true);
    }
  }, [checkoutCompletedPrior, user]);

  return (
    <div className="checkout">
      <div className="checkout-content">
        <SudowriteLogo
          className="logo"
          size="xxx-large"
        />

        {finished && (
          <>
            <h1>Thank you!</h1>
            <h2>
              <p>We really appreciate your support of Sudowrite!</p>
              <p>
                Next, be sure to join our <a href={DISCORD_LINK}>Community Discord</a> to learn how to get the most out your membership. And have fun!
              </p>
            </h2>
            {subscription?.data?.hasPaid && !requiresConferencePayment(subscription.data) ? (
              <SudoButton
                className="start-writing-button"
                variant="primary"
                size="default"
                onClick={() => {
                  window.location.href = nextUrl || "/app/";
                }}
              >
                Start Writing!
              </SudoButton>
            ) : (
              <p>Setting up your subscription...</p>
            )}
          </>
        )}

        {!finished && (
          <>
            <h1>Hold tight, we’re almost there…</h1>
            <h2>
              <span>
                (If you’re stuck here for more than a minute, <a href="mailto:hi@sudowrite.com">please let us know</a>.)
              </span>
            </h2>
          </>
        )}
      </div>
    </div>
  );
}
